import React, { useState, useEffect } from 'react';
import { ArrowRight, PlayCircle, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { heroBanners } from '../mock';

export default function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % heroBanners.length), 5500);
    return () => clearInterval(t);
  }, [paused]);

  const go = (n) => setIdx((idx + n + heroBanners.length) % heroBanners.length);

  return (
    <section
      id="home"
      className="relative pt-24 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[560px] md:h-[600px] w-full">
        {heroBanners.map((b, i) => {
          const active = i === idx;
          const accentColor = b.accent === 'orange' ? '#FF7A00' : '#1F8B4C';
          return (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-1000 ${active ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            >
              <img src={b.image} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(90deg, rgba(15,43,29,0.85) 0%, rgba(15,43,29,0.55) 45%, rgba(15,43,29,0.1) 100%)`,
                }}
              />
              <div className="container-x relative z-10 h-full flex items-center">
                <div className="max-w-2xl text-white fade-up" key={idx}>
                  <div
                    className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-1.5 rounded-full mb-5 border border-white/20"
                  >
                    <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: accentColor === '#FF7A00' ? '#FFB629' : '#26A45A' }} />
                    <span className="text-[11px] font-bold uppercase tracking-[3px]" style={{ color: accentColor === '#FF7A00' ? '#FFD98A' : '#B8ECC9' }}>{b.eyebrow}</span>
                  </div>
                  <h1 className="font-black leading-[1.05] tracking-tight text-[38px] sm:text-[52px] lg:text-[62px]">
                    {b.title}
                  </h1>
                  <p className="mt-5 text-white/85 text-lg leading-relaxed max-w-xl">{b.sub}</p>

                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link to="/contact" className="btn-primary" style={{ background: accentColor }}>
                      Get Started <ArrowRight size={18} />
                    </Link>
                    <Link to="/services" className="px-6 py-3 rounded-full bg-white/10 border border-white/30 backdrop-blur text-white font-semibold text-sm hover:bg-white/20 transition inline-flex items-center gap-2">
                      <PlayCircle size={18} /> Explore Services
                    </Link>
                  </div>

                  <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2">
                    {['30,000+ Sellers', '8+ Marketplaces', 'Dedicated Manager'].map((t) => (
                      <div key={t} className="flex items-center gap-2 text-sm text-white/90">
                        <CheckCircle2 size={16} style={{ color: accentColor === '#FF7A00' ? '#FFB629' : '#7FE0A0' }} />
                        {t}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Controls */}
        <button
          onClick={() => go(-1)}
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/15 backdrop-blur border border-white/25 items-center justify-center text-white hover:bg-white/30 transition"
          aria-label="Previous"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          onClick={() => go(1)}
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/15 backdrop-blur border border-white/25 items-center justify-center text-white hover:bg-white/30 transition"
          aria-label="Next"
        >
          <ChevronRight size={22} />
        </button>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroBanners.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-2 rounded-full transition-all ${i === idx ? 'w-10 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
